import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import { notifyError } from "./error";

const RequestMockSymbol = Symbol.for(__DEV__ ? "__RequestMock__" : "");
export type Request<T> = AxiosRequestConfig<T> & {
  [RequestMockSymbol]?: MockDataType<T> | (() => MockDataType<T>);
};

export type KscResponseModel<T = unknown> = {
  RequestId: string;
  Data: T;
};

export type GenericRequestCreator = <T = unknown>(
  method: string,
  config: AxiosRequestConfig,
) => Request<T>;

export type RequestCreator = <T = unknown, D = any>(config: AxiosRequestConfig<D>) => Request<T>;

export type RequestFactory = {
  request: GenericRequestCreator;
  get: RequestCreator;
  post: RequestCreator;
  put: RequestCreator;
  patch: RequestCreator;
  del: RequestCreator;
};

/** 30s */
export const DEFAULT_TIMEOUT = 30000;

export function createRequestFactory(defaultConfig?: AxiosRequestConfig): RequestFactory {
  const realDefaultConfig = Object.assign(
    {
      timeout: DEFAULT_TIMEOUT,
    },
    defaultConfig,
  );

  const headers = (defaultConfig || {}).headers || {};
  const commonHeaders = headers.common;

  realDefaultConfig.headers = {
    ...headers,
    common: Object.assign({}, commonHeaders),
  };

  function request<T = unknown, D = any>(
    method: string,
    config?: AxiosRequestConfig<D>,
  ): Request<T> {
    return {
      ...realDefaultConfig,
      ...config,
      method,
    };
  }

  return {
    request,
    get<T = unknown, D = any>(config: AxiosRequestConfig<D>) {
      return request<T, D>("get", config);
    },
    post<T = unknown, D = any>(config: AxiosRequestConfig<D>) {
      return request<T, D>("post", config);
    },
    put<T = unknown, D = any>(config: AxiosRequestConfig<D>) {
      return request<T, D>("put", config);
    },
    del<T = unknown, D = any>(config: AxiosRequestConfig<D>) {
      return request<T, D>("delete", config);
    },
    patch<T = unknown, D = any>(config: AxiosRequestConfig<D>) {
      return request<T, D>("patch", config);
    },
  };
}

export const httpclient = createRequestFactory();

// export type RequiredFields<
//   T extends object,
//   K extends keyof T,
//   OK extends keyof T = Exclude<keyof T, K>,
// > = Pick<
//   {
//     [KK in OK]: T[KK];
//   } & {
//     [KK in K]-?: T[KK];
//   },
//   OK | K
// >;
//
// export type MockDataType<D> = RequiredFields<Partial<Omit<AxiosResponse<D>, "config">>, "data">;

export interface MockDataType<T = any> {
  data: T;
  status?: number;
  statusText?: string;
  headers?: Record<string, string>;
}

export function mock<
  D extends object,
  T extends any[],
  K extends MockDataType<D> | ((...args: T) => MockDataType<D>),
>(mockData: K, config: (...args: T) => Request<D>) {
  return (...args: T) => {
    const c = config(...args);
    c[RequestMockSymbol] =
      typeof mockData === "function"
        ? (mockData as (...args: any[]) => any).bind(null, ...args)
        : mockData;
    return c;
  };
}

function getMockData<D extends object>(config: Request<D>) {
  if (Object.prototype.hasOwnProperty.call(config as any, RequestMockSymbol)) {
    const mockData = config[RequestMockSymbol];
    if (typeof mockData === "function") {
      return mockData();
    }
    return mockData;
  }
  return undefined;
}

export function send<D extends object = any>(config: Request<D>) {
  const mockData = getMockData(config);
  if (mockData !== void 0) {
    __DEV__ && console.warn("use mock data");
    return {
      status: 200,
      statusText: "success",
      headers: {},
      ...mockData,
      config: config as any,
    } satisfies AxiosResponse<D, any>;
  }

  return axios<D>(config);
}

/**
 *
 * @param action 描述是干什么的
 * @param errorHandler 可选的错误处理函数，如果返回值为 false，将不会执行 notifyError
 * @returns
 */
export function notifyOnError(
  action: string | (() => string) = "",
  errorHandler?: (e: unknown) => boolean | undefined,
) {
  return function wrapper<T, A extends any[]>(fn: (...args: A) => Promise<T>) {
    return (...args: A) =>
      fn(...args).catch(e => {
        const continues = errorHandler?.(e);
        if (continues === false) {
          return;
        }
        notifyError(typeof action === "string" ? action : action(), e);
      });
  };
}
